export const DOCUMENT_LINK_PATH = '/documento';
export const DOCUMENT_LINK_QUERY_PARAM = 'documento';
export const SHORT_LINK_PATH = '/d';

function buildRootSharePath(search = '') {
  return search ? `/?${search.replace(/^\?/, '')}` : `/?${DOCUMENT_LINK_QUERY_PARAM}=1`;
}

function parseExistingShareUrl(value) {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';

  if (trimmedValue.startsWith(`${DOCUMENT_LINK_PATH}?`) || trimmedValue === DOCUMENT_LINK_PATH) {
    const query = trimmedValue.split('?')[1] || '';
    const params = new URLSearchParams(query);
    params.set(DOCUMENT_LINK_QUERY_PARAM, '1');
    return buildRootSharePath(params.toString());
  }

  if (trimmedValue.startsWith(`/?`)) {
    const params = new URLSearchParams(trimmedValue.slice(2));
    if (params.get(DOCUMENT_LINK_QUERY_PARAM) === '1') {
      return trimmedValue;
    }
  }

  if (trimmedValue.startsWith(`${SHORT_LINK_PATH}/`)) {
    return trimmedValue;
  }

  try {
    const parsedUrl = new URL(trimmedValue, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    if (parsedUrl.pathname === DOCUMENT_LINK_PATH) {
      const params = new URLSearchParams(parsedUrl.search);
      params.set(DOCUMENT_LINK_QUERY_PARAM, '1');
      return buildRootSharePath(params.toString());
    }
    if (parsedUrl.pathname === '/' && parsedUrl.searchParams.get(DOCUMENT_LINK_QUERY_PARAM) === '1') {
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    }
    if (parsedUrl.pathname.startsWith(`${SHORT_LINK_PATH}/`)) {
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    }
  } catch {
    return '';
  }

  return '';
}

function readDocumentField(document, keys) {
  for (const key of keys) {
    const value = document?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }

  return '';
}

function normalizeHttpUrl(value) {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';

  try {
    const parsedUrl = new URL(trimmedValue);
    return ['http:', 'https:'].includes(parsedUrl.protocol) ? parsedUrl.toString() : '';
  } catch {
    return '';
  }
}

export function buildDocumentSharePath(document = {}, options = {}) {
  const existingSharePath = parseExistingShareUrl(readDocumentField(document, ['url']));
  if (existingSharePath) return existingSharePath;

  const officialUrl = normalizeHttpUrl(readDocumentField(document, ['officialUrl', 'url']));
  const params = new URLSearchParams();
  params.set(DOCUMENT_LINK_QUERY_PARAM, '1');

  if (officialUrl) params.set('url', officialUrl);

  const name = readDocumentField(document, ['nome', 'name', 'title']);
  const portal = String(options.portalName || '').trim() || readDocumentField(document, ['portalName', 'portal', 'sistema']);
  const type = readDocumentField(document, ['tipo', 'type']);
  const date = readDocumentField(document, ['data', 'documentDate', 'document_date_br']);
  const system = readDocumentField(document, ['sistema', 'system']);
  const description = readDocumentField(document, ['description', 'resumo', 'summary']);
  const discontinued = document.discontinued ? '1' : '';
  const replacedByName = readDocumentField(document, ['replacedByName']);
  const replacedByUrl = readDocumentField(document, ['replacedByUrl']);

  if (name) params.set('nome', name);
  if (portal) params.set('portal', portal);
  if (type) params.set('tipo', type);
  if (date) params.set('data', date);
  if (system) params.set('sistema', system);
  if (description) params.set('description', description);
  if (discontinued) params.set('discontinued', discontinued);
  if (replacedByName) params.set('replacedByName', replacedByName);
  if (replacedByUrl) params.set('replacedByUrl', replacedByUrl);

  const query = params.toString();
  return buildRootSharePath(query);
}

export function buildDocumentShareUrl(document = {}, options = {}) {
  const path = buildDocumentSharePath(document, options);
  const explicitBaseUrl = String(options.baseUrl || '').trim().replace(/\/+$/, '');

  if (explicitBaseUrl) {
    return `${explicitBaseUrl}${path}`;
  }

  if (typeof window === 'undefined') {
    return path;
  }

  return `${window.location.origin}${path}`;
}

export function parseDocumentShareParams(search = '') {
  const params = new URLSearchParams(search);

  return {
    officialUrl: normalizeHttpUrl(params.get('url')),
    nome: String(params.get('nome') || '').trim(),
    portal: String(params.get('portal') || '').trim(),
    tipo: String(params.get('tipo') || '').trim(),
    data: String(params.get('data') || '').trim(),
    sistema: String(params.get('sistema') || '').trim(),
    description: String(params.get('description') || params.get('resumo') || '').trim(),
    discontinued: params.get('discontinued') === '1',
    replacedByName: String(params.get('replacedByName') || '').trim(),
    replacedByUrl: String(params.get('replacedByUrl') || '').trim()
  };
}
